/**
 * Agent Approval-history feature — query hooks + the CSV export URL.
 *
 * Tenant-scoped, READ-ONLY surface: GET /api/v1/tenants/{tenant_id}/agents/approval-history.
 * There are no mutations — a T3 decision is passkey-bound to the delegating human; admins
 * observe and export only.
 */

import { useQuery } from "@tanstack/react-query";
import { apiFetch, type ApiError } from "@/lib/api-client";
import { queryKeys } from "@/lib/query-keys";
import {
  approvalHistoryResponseSchema,
  type ApprovalHistoryResponse,
  type ApprovalHistorySearch,
} from "./schemas";

const PAGE_SIZE = 50;

function base(tenantID: string): string {
  return `/api/v1/tenants/${encodeURIComponent(tenantID)}/agents/approval-history`;
}

// filterParams = the filters that define WHICH rows (not pagination). BOTH the paginated JSON
// fetch and the full CSV export consume this, so the export can never silently drift from the
// filtered view (e.g. a future since/until added here flows to both automatically).
function filterParams(search: ApprovalHistorySearch): Record<string, string> {
  const p: Record<string, string> = {};
  if (search.status) p.status = search.status;
  return p;
}

function searchParams(search: ApprovalHistorySearch): Record<string, string | number> {
  return { ...filterParams(search), limit: PAGE_SIZE, offset: search.offset };
}

export async function fetchApprovalHistory(
  tenantID: string,
  search: ApprovalHistorySearch,
): Promise<ApprovalHistoryResponse> {
  const raw = await apiFetch<unknown>(base(tenantID), { searchParams: searchParams(search) });
  return approvalHistoryResponseSchema.parse(raw);
}

export function useApprovalHistoryQuery(tenantID: string, search: ApprovalHistorySearch) {
  // Key on the fields the FETCH consumes (status/offset) — `days` only scopes the
  // analytics panel above the log, so it must not re-key (and refetch) the log itself.
  const keyed = { status: search.status, offset: search.offset } as ApprovalHistorySearch;
  return useQuery<ApprovalHistoryResponse, ApiError>({
    queryKey: queryKeys.agents.approvalHistory(tenantID, keyed),
    queryFn: () => fetchApprovalHistory(tenantID, search),
    // Decisions land in seconds-to-minutes; a modest poll keeps a still-pending row fresh
    // without hammering an audit surface.
    refetchInterval: 30_000,
  });
}

export const APPROVAL_HISTORY_PAGE_SIZE = PAGE_SIZE;

// Build the CSV export URL. The caller navigates to it via <a href download> so the browser's
// native streaming + download dialog is used (the export can be large; keeps memory flat).
export function approvalHistoryExportURL(tenantID: string, search: ApprovalHistorySearch): string {
  // No limit/offset: the backend streams the FULL filtered set for format=csv (a compliance
  // export must be complete). Only the view filters are forwarded.
  const params = new URLSearchParams({ format: "csv", ...filterParams(search) });
  return `${base(tenantID)}?${params.toString()}`;
}
